import { FunctionComponent } from "react";

import { faCircleInfo, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { useAppDispatch } from "@/redux/hooks";

import { useDismissDashboardIssueMutation } from "@/pages/Private/redux/dashboard/dashboard.api";
import { removeIssueLocally } from "@/pages/Private/redux/dashboard/dashboard.slice";

import { DashboardIssue } from "../schema/dashboard";
import { DashboardBox } from "./DashboardBox";
import { DashboardIssueRow } from "./DashboardIssueRow";

interface IssuesBoxProps {
	issues: DashboardIssue[];
}

const compareIssues = (a: DashboardIssue, b: DashboardIssue): number => {
	if (a.severity !== b.severity) {
		return a.severity === "error" ? -1 : 1;
	}

	return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime();
};

const CountPill: FunctionComponent<{ count: number }> = ({ count }) => (
	<span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md text-[11px] font-semibold bg-status-error-light text-status-error mr-1">
		{count}
	</span>
);

export const IssuesBox: FunctionComponent<IssuesBoxProps> = ({ issues }) => {
	const dispatch = useAppDispatch();
	const [dismissIssue] = useDismissDashboardIssueMutation();

	const sorted = [...issues].sort(compareIssues);

	const handleDismiss = (issue: DashboardIssue) => {
		dispatch(removeIssueLocally({ type: issue.type, entityId: issue.entityId }));
		dismissIssue({ type: issue.type, entityId: issue.entityId }).catch(() => {
			// optimistic; backend re-arms on its own conditions
		});
	};

	const handleActivated = (issue: DashboardIssue) => {
		// Outreach was activated, so the underlying condition is gone — drop the issue locally.
		// The backend re-detects on its own if the list is later paused again.
		dispatch(removeIssueLocally({ type: issue.type, entityId: issue.entityId }));
	};

	if (sorted.length === 0) {
		return (
			<DashboardBox
				accent="green"
				dismissible={false}
				icon={<Icon className="w-4 h-4" icon={faCircleInfo} />}
				title="No issues"
			>
				<div className="px-4 py-6 text-center text-xs text-text-meta">
					Lists, syncs, and tracking are all running smoothly.
				</div>
			</DashboardBox>
		);
	}

	return (
		<DashboardBox
			accent="red"
			dismissible={false}
			icon={<Icon className="w-4 h-4" icon={faTriangleExclamation} />}
			subtitle="Fix these to keep your lists, syncs, and tracking running"
			title={
				<>
					<CountPill count={sorted.length} />
					Issues found
				</>
			}
		>
			{sorted.map((issue) => (
				<DashboardIssueRow
					key={`${issue.type}-${issue.entityId ?? "none"}`}
					issue={issue}
					onActivated={() => handleActivated(issue)}
					onDismiss={() => handleDismiss(issue)}
				/>
			))}
		</DashboardBox>
	);
};
